import { CommonModule } from '@angular/common';
import { LocalizationModule } from '@abp/ng.core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { DwApiSelectDirective, DwApiSelectObservable } from '@digiwall/core';
import { AutoSizedDropdownDirective } from '@digiwall/ui-kit';
import { DwActiveLookupRequestDto, DwLookupRequestDto } from '@digiwall/goliath-core/proxy';
import { WorkOrderDayBookDto } from '@digiwall/goliath-documents/proxy';
import {
  SERVICE_WORK_ORDER_STEP_DROPDOWN_KEYS,
  SERVICE_WORK_ORDER_STEP_TEXT_KEYS,
  ServiceWorkOrderStepFilterOptionsService,
  ServiceWorkOrderStepPlanningFilterKey,
  ServiceWorkOrderStepPlanningFilterKeys,
} from '../../../planning-common/filters';
import {
  ApiDropdown,
  BaseBoardFilterComponent,
} from '../../board-filter/base-board-filter.component';
import { ServiceWorkOrderStepPlanningItemFieldsDto } from './service-work-order-step-planning-item-fields';

/**
 * Planner board filter for the ServiceWorkOrderStep data source. Supplies this source's filter keys,
 * their labels, field extraction and option-label resolution to the shared
 * {@link BaseBoardFilterComponent}, which owns all of the rendering + state + predicate plumbing.
 *
 * DayBook / InterventionType render as static option lists; Customer / Article / Equipment / Worker
 * render as lazy `dwApiSelect` lookups (labels resolved natively, results constrained to the configured
 * presets when ≥2 are set); Description / DisplayNumber / StepName are client-side free-text inputs.
 */
@Component({
  selector: 'document-service-work-order-step-board-filter',
  templateUrl: '../../board-filter/base-board-filter.component.html',
  styleUrl: '../../board-filter/base-board-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    LocalizationModule,
    NgSelectModule,
    AutoSizedDropdownDirective,
    DwApiSelectDirective,
  ],
})
export class ServiceWorkOrderStepBoardFilterComponent extends BaseBoardFilterComponent {
  private readonly options = inject(ServiceWorkOrderStepFilterOptionsService);

  protected readonly keys = ServiceWorkOrderStepPlanningFilterKeys;
  protected readonly dropdownKeys: readonly ServiceWorkOrderStepPlanningFilterKey[] =
    SERVICE_WORK_ORDER_STEP_DROPDOWN_KEYS;
  protected readonly textKeys: readonly ServiceWorkOrderStepPlanningFilterKey[] =
    SERVICE_WORK_ORDER_STEP_TEXT_KEYS;

  protected readonly labelKey: Record<ServiceWorkOrderStepPlanningFilterKey, string> = {
    [this.keys.dayBook]: 'Doc::DayBook',
    [this.keys.customer]: '::Customer',
    [this.keys.interventionType]: 'Doc::ServiceWorkOrderDoc:ServiceWorkOrderKind',
    [this.keys.article]: 'Doc::Article',
    [this.keys.equipment]: 'Product::Equipment:Equipment',
    [this.keys.worker]: 'Contact::Worker',
    [this.keys.description]: '::Description',
    [this.keys.displayNumber]: '::Number',
    [this.keys.stepName]: 'Doc::ProductionFolderLine:Step',
  };

  private readonly dayBooks = signal<WorkOrderDayBookDto[]>([]);

  // Lazy lookups; the loader constrains results to the configured presets.
  private readonly customerSelect: DwApiSelectObservable = dto => {
    this.applyPresetIds(this.keys.customer, dto);
    return this.options.customerSelect(dto as DwActiveLookupRequestDto<string>);
  };

  private readonly articleSelect: DwApiSelectObservable = dto => {
    this.applyPresetIds(this.keys.article, dto);
    return this.options.articleSelect(dto as DwActiveLookupRequestDto<string>);
  };

  private readonly equipmentSelect: DwApiSelectObservable = dto => {
    this.applyPresetIds(this.keys.equipment, dto);
    return this.options.equipmentSelect(dto as DwLookupRequestDto<string>);
  };

  private readonly workerSelect: DwApiSelectObservable = dto => {
    this.applyPresetIds(this.keys.worker, dto);
    return this.options.workerSelect(dto as DwActiveLookupRequestDto<string>);
  };

  protected override readonly apiDropdowns: Record<string, ApiDropdown> = {
    [this.keys.customer]: { apiSelect: this.customerSelect, bindLabel: 'name' },
    [this.keys.article]: { apiSelect: this.articleSelect, bindLabel: 'code' },
    [this.keys.equipment]: { apiSelect: this.equipmentSelect, bindLabel: 'name' },
    [this.keys.worker]: { apiSelect: this.workerSelect, bindLabel: 'code' },
  };

  constructor() {
    super();
    this.options
      .loadDayBooks()
      .pipe(takeUntilDestroyed())
      .subscribe(dayBooks => this.dayBooks.set(dayBooks));
  }

  // ── Static option-label resolution (DayBook / InterventionType) ─────────────────

  private readonly kindLabels = computed(
    () => new Map(this.options.interventionTypes().map(e => [e.id, e.name?.current ?? e.id])),
  );

  private readonly dayBookLabels = computed(
    () => new Map(this.dayBooks().map(d => [d.id ?? '', d.name?.current ?? d.id ?? ''])),
  );

  protected fieldValue(key: string, displayFields: unknown): string | undefined {
    const fields = (displayFields ?? {}) as ServiceWorkOrderStepPlanningItemFieldsDto;
    switch (key) {
      case this.keys.dayBook:
        return fields.dayBookId;
      case this.keys.customer:
        return fields.customerId;
      case this.keys.interventionType:
        return fields.kind;
      case this.keys.article:
        return fields.articleId;
      case this.keys.equipment:
        return fields.equipmentId;
      case this.keys.worker:
        return fields.workerId;
      case this.keys.description:
        return fields.description;
      case this.keys.displayNumber:
        return fields.displayNumber;
      case this.keys.stepName:
        return fields.stepName;
      default:
        return undefined;
    }
  }

  protected optionLabel(key: string, id: string): string {
    switch (key) {
      case this.keys.interventionType:
        return this.kindLabels().get(id) ?? id;
      case this.keys.dayBook:
        return this.dayBookLabels().get(id) ?? id;
      default:
        return id; // Customer / Article / Equipment / Worker are api-select (labels resolved by the lookup).
    }
  }
}
